import { Injectable } from '@angular/core';
import { isEmpty } from '@app/core/functions/isEmpty';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { SurfaceTierThemeType } from '@app/core/services/surface-tier-theme.service';

const TIER_PLACEHOLDER: string = '[TIER]';

// Use the format '[feature]-[tier (optional)]-[page or component (optional)]'
export type SurfaceUpgradeFeatureProp =
    'attribute-mapping' |
    'bulk-overrides' |
    'column-stats' |
    'data-validation' |
    'error-mode' |
    'import-joins-full-service' |
    'import-joins-retail' |
    'overrides' |
    'sku-limit-approaching-full-service-products' |
    'sku-limit-approaching-full-service-alert' |
    'sku-limit-approaching-full-service-modal' |
    'sku-limit-approaching-retail-products' |
    'sku-limit-approaching-retail-alert' |
    'sku-limit-approaching-retail-modal' |
    'sku-limit-reached-full-service-products' |
    'sku-limit-reached-full-service-alert' |
    'sku-limit-reached-full-service-modal' |
    'sku-limit-reached-retail-products' |
    'sku-limit-reached-retail-alert' |
    'sku-limit-reached-retail-modal' |
    'taxonomy' |
    'transformers'
;

export type SurfaceUpgradeFeatureType = {
    name: SurfaceUpgradeFeatureProp;
    /**
     * HTML string for the description of this feature. Could include strong tags.
     */
    descriptionHTML: string;
    popupOrientation?: 'vertical' | 'horizontal';
    /**
     * Optional title HTML text to include when this feature is shown. If not provided,
     * will default to "Upgrade to <tier>"
     */
    titleHTML?: string;
    /**
     * The URL of the image to display in the popup.
     */
    imageURL?: string;
    /**
     * Optional text for a plans button.
     * If not provided, will default to "Try it out".
     */
    plansButtonText?: string;
    /**
     * Optional text for a channel assignments button.
     */
    productsButtonText?: string;

    inlineTextLinks?: { link?: 'products' | 'plans'; text: string; }[];
    /**
     * Optional for if this feature can be permanently dismissed via the popup.
     */
    rememberDismissed?: boolean;
};

export const SURFACE_UPGRADE_FEATURES: Record<SurfaceUpgradeFeatureProp, SurfaceUpgradeFeatureType> = {
    'attribute-mapping': {
        name: 'attribute-mapping',
        descriptionHTML: 'Customize how your product fields map to each channel. Send the right data, in the right format, every time.',
        popupOrientation: 'horizontal',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/attribute_mapping.svg'
    },
    'taxonomy': {
        name: 'taxonomy',
        descriptionHTML: 'View channel requirements directly within the Taxonomy page. Use it to search for valid product categories and identify required vs. optional attributes for each channel.',
        popupOrientation: 'horizontal',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/taxonomy.svg'
    },
    'import-joins-retail': {
        name: 'import-joins-retail',
        descriptionHTML: 'Bring in data from multiple sources to strengthen your catalog. Build a stronger dataset and unlock better results.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/import_joins.svg'
    },
    'import-joins-full-service': {
        name: 'import-joins-full-service',
        descriptionHTML: 'Enrich your product listings with data from external sources. Import files and automatically merge them with your catalog by matching SKUs.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/import_joins.svg',
        plansButtonText: 'Request a demo'
    },
    'transformers': {
        name: 'transformers',
        descriptionHTML: 'Automate feed cleanup and optimization with smart rules that enhance your product data and reduce manual work.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/transformers.svg'
    },
    'data-validation': {
        name: 'data-validation',
        descriptionHTML: 'Automatically check your data against each channel\'s requirements. Fix issues efficiently and keep your feeds error-free.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/data_validation.svg'
    },
    'column-stats': {
        name: 'column-stats',
        descriptionHTML: 'Get quick insights into your product data. Spot duplicates, check unique values, and pivot across columns to uncover patterns faster.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/column_stats.svg'
    },
    'bulk-overrides': {
        name: 'bulk-overrides',
        descriptionHTML: 'Download your product data in bulk, make updates offline (e.g., Excel, Google Sheets), and re-upload to apply changes instantly.',
        popupOrientation: 'vertical',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/bulk_overrides.svg'
    },
    'sku-limit-approaching-full-service-products': {
        name: 'sku-limit-approaching-full-service-products',
        popupOrientation: 'vertical',
        descriptionHTML: `You're almost at the SKU limit for ${TIER_PLACEHOLDER}. Unlock more SKUs to keep your growth on track, or manage your SKUs in BigCommerce.`,
        titleHTML: 'Almost at SKU limit',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/sku_limit_approaching_full_service.svg',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-approaching-full-service-alert': {
        name: 'sku-limit-approaching-full-service-alert',
        descriptionHTML: `# of your channels are almost at the SKU limit for ${TIER_PLACEHOLDER}. Unlock more SKUs to ensure all your products stay synced, or manage your SKUs in BigCommerce.`,
        titleHTML: 'Almost at SKU limit',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-approaching-full-service-modal': {
        name: 'sku-limit-approaching-full-service-modal',
        descriptionHTML: `<span class="fw-semibold">This channel is almost at the SKU limit for ${TIER_PLACEHOLDER}.</span>`,
        inlineTextLinks: [{ link: 'plans', text: 'Unlock more SKUs' }, { text: 'to keep your growth on track, or'}, { link: 'products', text: 'manage your SKUs in BigCommerce'}]
    },
    'sku-limit-approaching-retail-products': {
        name: 'sku-limit-approaching-retail-products',
        popupOrientation: 'vertical',
        descriptionHTML: 'You\'re almost at your SKU limit. Unlock more SKUs to keep your growth on track, or manage your SKUs in BigCommerce.',
        titleHTML: 'Almost at SKU limit',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/sku_limit_approaching_retail.svg',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-approaching-retail-alert': {
        name: 'sku-limit-approaching-retail-alert',
        descriptionHTML: '# of your channels are almost at your SKU limit. Unlock more SKUs to keep your growth on track, or manage your SKUs in BigCommerce. ',
        titleHTML: 'Almost at SKU limit',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-approaching-retail-modal': {
        name: 'sku-limit-approaching-retail-modal',
        descriptionHTML: '<span class="fw-semibold">This channel is almost at your SKU limit.</span>',
        inlineTextLinks: [{ link: 'plans', text: 'Unlock more SKUs' }, { text: 'to keep your growth on track, or'}, { link: 'products', text: 'manage your SKUs in BigCommerce'}]
    },
    'sku-limit-reached-full-service-products': {
        name: 'sku-limit-reached-full-service-products',
        popupOrientation: 'vertical',
        descriptionHTML: `You've reached the SKU limit for ${TIER_PLACEHOLDER}. Unlock more SKUs to ensure all your products stay synced, or manage your SKUs in BigCommerce.`,
        titleHTML: 'SKU limit reached',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/sku_limit_reached_retail.svg',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-reached-full-service-alert': {
        name: 'sku-limit-reached-full-service-alert',
        descriptionHTML: `# of your channels reached the SKU limit for ${TIER_PLACEHOLDER}. Unlock more SKUs to ensure all your products stay synced, or manage your SKUs in BigCommerce.`,
        titleHTML: 'You\'ve reached the SKU limit',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-reached-full-service-modal': {
        name: 'sku-limit-reached-full-service-modal',
        descriptionHTML: `<span class="fw-semibold">This channel reached the SKU limit for ${TIER_PLACEHOLDER}, so any assigned SKUs past the limit won't be synced to the channel.</span>`,
        inlineTextLinks: [{ link: 'plans', text: 'Unlock more SKUs' }, { text: 'to ensure all your products stay synced, or'}, { link: 'products', text: 'manage your SKUs in BigCommerce'}]
    },
    'sku-limit-reached-retail-products': {
        name: 'sku-limit-reached-retail-products',
        popupOrientation: 'vertical',
        descriptionHTML: 'You\'ve reached your SKU limit. Unlock more SKUs to ensure all your products stay synced, or manage your SKUs in BigCommerce.',
        titleHTML: 'SKU limit reached',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/sku_limit_reached_retail.svg',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-reached-retail-alert': {
        name: 'sku-limit-reached-retail-alert',
        descriptionHTML: '# of your channels reached your SKU limit. Unlock more SKUs to ensure all your products stay synced, or manage your SKUs in BigCommerce.',
        titleHTML: 'You\'ve reached your SKU limit',
        plansButtonText: 'Unlock more SKUs',
        productsButtonText: 'Manage SKUs'
    },
    'sku-limit-reached-retail-modal': {
        name: 'sku-limit-reached-retail-modal',
        descriptionHTML: '<span class="fw-semibold">This channel reached your SKU limit, so any assigned SKUs past the limit won\'t be synced to the channel.</span>',
        inlineTextLinks: [{ link: 'plans', text: 'Unlock more SKUs' }, { text: 'to ensure all your products stay synced, or'}, { link: 'products', text: 'manage your SKUs in BigCommerce'}]
    },
    'error-mode': {
        name: 'error-mode',
        popupOrientation: 'vertical',
        descriptionHTML: 'Struggling with errors? Surface Retail gives you advanced tools that <span class="fw-semibold">catch issues early</span> and <span class="fw-semibold">resolve them in bulk</span>. Save time, stay compliant, and keep your products live without the manual cleanup.',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/error_mode.svg',
        rememberDismissed: true
    },
    'overrides': {
        name: 'overrides',
        popupOrientation: 'vertical',
        descriptionHTML: 'Updating product data one field at a time requires extra effort. Surface Retail enables you to <span class="fw-semibold">update your data in bulk</span> and set up <span class="fw-semibold">automated rules</span> to optimize data at scale. Get consistent, accurate product feeds without the repetitive work.',
        imageURL: '/dist/app/assets/images/surface-upgrade-features/overrides.svg'
    }
};

export const TRY_IT_OUT: string = 'Try it out';

/**
 * A key used for keeping track if the user has saved a total of 10 overrides before.
 */
const HAS_SAVED_10_OVERRIDES_KEY: string = 'surface-has-saved-10-overrides';
/**
 * A key used for keeping track of the number of overrides the user has saved. Will be reset
 * to 0 after telling the calling component to show the overrides popup.
 */
const NUM_SAVED_OVERRIDES_KEY: string = 'surface-num-saved-overrides';

@Injectable({
    providedIn: 'root'
})
export class SurfaceUpgradeFeatureService {
    constructor(
        private readonly localStorageService: LocalStorageService
    ) {}

    getUpgradeFeatureType(featureName: SurfaceUpgradeFeatureProp): SurfaceUpgradeFeatureType {
        return SURFACE_UPGRADE_FEATURES[featureName] ?? null;
    }

    /**
     * Gets the HTML string for the description of a particular feature, replacing the [TIER] placeholder
     * with the display name of the provided theme.
     * @param featureType the feature we're displaying
     * @param theme the theme associated with the feature
     * @returns the HTML string for the description
     */
    getDescriptionHTML(featureType: SurfaceUpgradeFeatureType, theme: SurfaceTierThemeType): string {
        return featureType.descriptionHTML.replaceAll(TIER_PLACEHOLDER, theme.displayName);
    }

    /**
     * Gets the HTML string for the title of a particular feature. If the feature has a titleHTML attribute,
     * we'll use that. Otherwise, we'll default to "Upgrade to <tier>". If includeTierClass is true (by default), we'll
     * wrap the tier in a span with a class of text-<tier>. Otherwise, we'll just return the plain text.
     * @param featureType the feature we're displaying
     * @param theme the theme associated with the feature
     * @param includeTierClass whether to include the tier class in the returned HTML
     * @returns the HTML string for the title
     */
    getTitleHTML(featureType: SurfaceUpgradeFeatureType, theme: SurfaceTierThemeType, includeTierClass: boolean = true): string {
        if (featureType.titleHTML) {
            return featureType.titleHTML;
        } else if (includeTierClass) {
            return `Upgrade to <span class="text-${theme.tier}">${theme.displayName}</span>`;
        }
        return `Upgrade to ${theme.displayName}`;
    }

    /**
     * Gets the text for the primary CTA button for a particular feature. If the feature has a plansButtonText attribute,
     * we'll use that. Otherwise, we'll default to "Try it out".
     * @param featureType the feature we're displaying
     * @returns the text for the primary CTA button
     */
    getPlansButtonText(featureType: SurfaceUpgradeFeatureType): string {
        if (featureType.plansButtonText) {
            return featureType.plansButtonText;
        }

        // TODO: decide if we want to show "Try it for free" instead, depending on if the user has already started a trial

        return TRY_IT_OUT;
    }

    private getFeatureDismissedKey(exportId: string, featureName: SurfaceUpgradeFeatureProp): string {
        return `${exportId}-${featureName}-surface-upgrade-popup-dismissed`;
    }

    hasFeaturePopoverDismissed(exportId: string, featureName: SurfaceUpgradeFeatureProp): boolean {
        return !isEmpty(this.localStorageService.getItem(this.getFeatureDismissedKey(exportId, featureName)));
    }

    rememberFeaturePopoverDismissed(exportId: string, featureName: SurfaceUpgradeFeatureProp): void {
        this.localStorageService.setItem(this.getFeatureDismissedKey(exportId, featureName), 'true');
    }

    private hasSaved10Overrides(): boolean {
        return this.localStorageService.getItem(HAS_SAVED_10_OVERRIDES_KEY) === 'true';
    }

    private setHasSaved10Overrides(): void {
        this.localStorageService.setItem(HAS_SAVED_10_OVERRIDES_KEY, 'true');
    }

    private getSavedOverridesCount(): number {
        const previousCount: number = parseInt(this.localStorageService.getItem(NUM_SAVED_OVERRIDES_KEY) || '0', 10);
        if (isNaN(previousCount)) {
            return 0;
        }

        return previousCount;
    }

    private setSavedOverridesCount(count: number): void {
        this.localStorageService.setItem(NUM_SAVED_OVERRIDES_KEY, count.toString());
    }

    /**
     * Checks whether we should show the overrides popup based on the number of overrides we're currently trying to add
     * plus the number of overrides we've added in the past (stored in local storage). If the total reaches 10,
     * we'll return true and reset the count in local storage. If not, we'll update the count in local storage
     * and return false. After we have reached 10 overrides once, we'll then show the popup after every 100th override.
     * @param numOverridesToAdd
     * @returns
     */
    shouldShowOverridesPopup(numOverridesToAdd: number): boolean {
        if (!this.hasSaved10Overrides()) {
            const totalOverrides: number = this.getSavedOverridesCount() + numOverridesToAdd;
            if (totalOverrides >= 10) {
                this.setHasSaved10Overrides();
                this.setSavedOverridesCount(0);
                return true;
            }

            this.setSavedOverridesCount(totalOverrides);
            return false;
        }

        const totalOverrides: number = this.getSavedOverridesCount() + numOverridesToAdd;
        if (totalOverrides >= 100) {
            this.setSavedOverridesCount(0);
            return true;
        }

        this.setSavedOverridesCount(totalOverrides);
        return false;
    }
}
